import { AUTH_API_HOST } from "@/api/apis";
import { readGaIdentifiersFromBrowser } from "@/libs/analytics/ga-cookie";
import { CartToken } from "@/libs/cart-token/CartToken";

export type ServerCartItem = { sku: string; quantity: number };
// standalone 게임아이템(서버 확정 형식 mgmt #1164: 식별자만, quantity 없음).
// Global-E GetCheckoutCartInfo 가 IsVirtual 제품으로 읽고 가격/이름은 카탈로그에서 resolve.
export type ServerGameItem = {
  gameResellId: string;
  platform: string;
};

let cachedToken: string | null = null;
let tokenExpiresAt = 0;

async function getToken(): Promise<string | null> {
  if (cachedToken && Date.now() < tokenExpiresAt) return cachedToken;
  try {
    const res = await fetch(`${AUTH_API_HOST}/api/token`, {
      credentials: "include",
    });
    if (!res.ok) return null;
    const result = await res.json();
    cachedToken = result.token;
    tokenExpiresAt = Date.now() + 10 * 60 * 1000;
    return cachedToken;
  } catch {
    return null;
  }
}

let debounceTimer: ReturnType<typeof setTimeout>;
// 마지막으로 syncCart 에 들어온 payload(debounce 대기 중). flushCartSync 가 즉시 밀어낼 때 사용.
let pendingSync: {
  items: ServerCartItem[];
  gameItems: ServerGameItem[];
} | null = null;
// 마지막으로 시작된 서버 동기화 promise — flushCartSync 가 in-flight 완료까지 기다릴 수 있게 추적.
let inFlightSync: Promise<void> = Promise.resolve();

/**
 * `bh_cart_token` 키의 게스트 카트에 items + standalone 게임아이템을 쓴다(무인증).
 * gameItems 는 Global-E GetCheckoutCartInfo 가 IsVirtual 제품으로 읽는다(§5.6, mgmt 스키마 확장 의존).
 * Global-E 가 이 토큰 카트를 읽으므로, 완료를 await 할 수 있게 promise 를 반환한다(flushCartSync).
 *
 * **게스트 카트 쓰기의 단일 관문이다** — `doSync`(debounced sync)와 `writeGuestCartForCheckout`
 * (buy-now full-nav)이 모두 여기로 모인다. GA4 식별자를 여기 한 곳에 실으면 GE 가 읽는 모든
 * 경로가 자동으로 덮인다(#5778).
 */
function postGuestCart(
  cartToken: string,
  items: ServerCartItem[],
  gameItems: ServerGameItem[] = [],
): Promise<void> {
  return fetch("/admin-api/api/v1/guest-cart/", {
    method: "POST",
    headers: { "content-type": "application/json" },
    // GA4 서버사이드 purchase(MP) 용 식별자 — mgmt 가 GE draft 생성 시 여기서 옮겨 싣는다
    // (mgmt#1276). GE draft 는 GE 서버가 만들어 브라우저 쿠키가 없어서 이 경유가 필요하다.
    // 쿠키가 없으면 키가 탈락하고, mgmt 는 빈 값이면 기존 값을 보존하므로 안전하다.
    body: JSON.stringify({
      cartToken,
      items,
      gameItems,
      ...readGaIdentifiersFromBrowser(),
    }),
  })
    .then(() => {})
    .catch(() => {});
}

/**
 * 실제 동기화 1회 수행 — uid 카트(로그인 primary, fire-and-forget) + guest-cart 토큰 미러(await 대상).
 * 토큰 미러 완료를 반환해 flushCartSync 가 기다릴 수 있게 한다.
 */
function doSync(
  items: ServerCartItem[],
  gameItems: ServerGameItem[],
): Promise<void> {
  return getToken().then((token) => {
    if (token) {
      // uid 카트: 앱 UI·교차기기용 primary. GE 는 이 경로를 안 읽으므로 fire-and-forget.
      fetch("/admin-api/api/v1/cart/", {
        method: "POST",
        headers: {
          "content-type": "application/json",
          authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ items, gameItems }),
      }).catch(() => {});
      // Global-E 읽기용 토큰 mirror. 아이템 있으면 토큰 보장(ensure), 비었으면 기존 토큰에만 반영.
      const cartToken =
        items.length > 0 || gameItems.length > 0
          ? CartToken.ensure()
          : CartToken.get();
      return cartToken ? postGuestCart(cartToken, items, gameItems) : undefined;
    }
    const cartToken = CartToken.get();
    if (!cartToken) return;
    return postGuestCart(cartToken, items, gameItems);
  });
}

/**
 * `doSync` 를 `inFlightSync` 큐에 이어붙인다 — 직전 sync 완료 후 실행해 **최신 payload 가 마지막에
 * 기록되도록 순서를 보장**한다(동시 실행 시 stale 이 나중에 착지하는 것 방지). debounce timer 경로와
 * flushCartSync 가 **같은 큐**를 써서 비대칭이 없다.
 * 체인의 모든 링크에서 에러를 삼켜 `inFlightSync` 가 **절대 reject 하지 않게** 한다 — flushCartSync 를
 * await 하는 체크아웃이 sync 실패(예: 쿠키 잠긴 env 의 `CartToken.ensure` 동기 throw)로 멈추면
 * 안 되기 때문(reject → `handleCheckout` 중단 → loading 고정 → 버튼 영구 멈춤).
 */
function enqueueSync(items: ServerCartItem[], gameItems: ServerGameItem[]) {
  inFlightSync = inFlightSync
    .catch(() => {})
    .then(() => doSync(items, gameItems))
    .catch(() => {});
}

/**
 * 지정 아이템을 **guest-cart(bh_cart_token)에만** 즉시 쓴다(await). uid `/cart`(로그인 primary)는
 * 건드리지 않는다 — buy-now/PDP 체크아웃이 GE 국제 체크아웃으로 라우팅될 때 GE 가 읽을 카트를 채우되,
 * 로그인 유저의 실제 카트를 오염시키지 않기 위함(docs §4.4 #2). guest-cart 미러는 이후 카트 활동 시
 * self-heal(cart-store subscribe → syncCart). postGuestCart 가 에러를 삼키므로 reject 하지 않는다.
 *
 * ⚠️ **`enqueueSync`/`inFlightSync` 순서 체인(#5015)을 의도적으로 우회한다** — full-nav 직전에 GE 가
 * 곧바로 읽을 카트이므로 debounce 없이 즉시 await 로 착지를 보장해야 하기 때문. 경쟁 write 와의 순서
 * 안전 근거: (a) **바로 결제**(cart 우회)는 debounced `syncCart` 가 없다 — 단 직전에 소비 token 가드가 카트를
 * 정리했다면(`clearCartIfConsumed`) 그 정리가 건 빈 sync 가 있을 수 있어, 정리 **안에서** `flushCartSync()` 로 먼저
 * 밀어낸다(token 이 없는 동안 밀어내 새 token 을 덮지 않게 — `consumed-cart.ts` `clearConsumedCart`). (b) **담고 결제**는
 * cart add 가 debounced `syncCart` 를 트리거하지만 이 write 와 **동일한 카트 스냅샷**이라 어느 쪽이
 * 나중에 착지해도 divergence 가 없다(같은 토큰·같은 items). 향후 buy-now 아이템과 debounced 내용이
 * 갈라지는 진입점이 생기면 이 가정이 깨지므로 그때 `enqueueSync` 경유로 통일할 것.
 */
export async function writeGuestCartForCheckout(
  items: ServerCartItem[],
  gameItems: ServerGameItem[] = [],
): Promise<void> {
  const cartToken = CartToken.ensure();
  if (!cartToken) return;
  await postGuestCart(cartToken, items, gameItems);
}

/**
 * 카트를 서버에 동기화한다(debounced 500ms, fire-and-forget). 실제 쓰기는 `doSync`.
 * - 로그인 유저: Bearer 로 `/api/v1/cart` (uid 카트, primary) + 토큰 guest-cart 에도 mirror.
 * - 게스트: `bh_cart_token` 으로 `/api/v1/guest-cart` (token 카트).
 *
 * 토큰 guest-cart mirror 이유: Global-E `GetCheckoutCartInfo` 는 Bearer 없이 cart token 만 받으므로
 * 토큰 한 경로로 게스트·로그인 카트를 모두 읽어야 한다. docs §3.1(B)·§4.0.
 * ⚠️ debounce/fetch 는 페이지 언로드로 취소되므로, GE 국제 체크아웃 full-nav 직전엔 `flushCartSync` 로
 *    강제 flush 할 것(안 하면 GE 가 stale 카트를 읽어 수량 어긋남 — #5013 후속).
 */
export function syncCart(
  items: ServerCartItem[],
  gameItems: ServerGameItem[] = [],
) {
  pendingSync = { items, gameItems };
  clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => {
    const p = pendingSync;
    pendingSync = null;
    if (p) enqueueSync(p.items, p.gameItems);
  }, 500);
}

/**
 * 대기(debounced) 중이거나 진행 중인 카트 동기화를 **즉시 밀어내고 완료까지 기다린다**.
 * Global-E 국제 체크아웃으로 full-nav(`window.location.href`) 하기 직전에 호출 — 안 하면 페이지
 * 언로드가 debounce 타이머/in-flight fetch 를 취소해 GE 가 `GetCheckoutCartInfo` 로 이전(stale)
 * guest-cart 를 읽어 수량·항목이 어긋난다(#5013 후속 버그). `enqueueSync` 로 직전 sync 뒤에 이어붙여
 * 최신 payload 가 마지막에 쓰이도록 순서를 보장하고, 체인이 에러를 삼키므로 **실패해도 reject 하지
 * 않는다**(체크아웃을 막지 않게 — enqueueSync 참고).
 */
export async function flushCartSync(): Promise<void> {
  clearTimeout(debounceTimer);
  const p = pendingSync;
  pendingSync = null;
  if (p) enqueueSync(p.items, p.gameItems);
  // enqueueSync 가 체인 끝에서 에러를 삼켜 inFlightSync 는 항상 resolve → 이 await 는 throw 하지 않는다.
  await inFlightSync;
}

/**
 * 로그인 유저가 현재 게스트 cart token 을 **자기 것으로 claim** — mgmt 가 `carts[cartToken].ownerUid`
 * 에 회원 uid 를 기록한다(uid 는 서버가 Bearer 에서만 도출, body 로 안 보냄 = uid 주입 표면 제거).
 * Global-E 국제 체크아웃 주문이 **회원으로 귀속**되게 하는 전제다(docs/global-e-send-order-contract.md
 * §2.5, mgmt #1179). `mergeGuestCart` 와 달리 **게스트 문서를 삭제하지 않으므로**(ownerUid 만 기록) GE 가
 * `GetCheckoutCartInfo`/`SendOrderToMerchant` 에서 그 토큰 카트를 계속 읽고 귀속할 수 있다.
 * 게스트 cartToken 이 없거나 로그인 토큰을 못 받으면 no-op. fire-and-forget.
 */
export async function claimGuestCartOwner(): Promise<void> {
  const cartToken = CartToken.get();
  if (!cartToken) return;
  const token = await getToken();
  if (!token) return;
  try {
    await fetch("/admin-api/api/v1/cart/guest-token/owner/", {
      method: "POST",
      headers: {
        "content-type": "application/json",
        authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ cartToken }),
    });
  } catch {
    /* fire-and-forget */
  }
}

/**
 * GE 국제 체크아웃으로 full-nav 하기 **직전에** 쿠폰 전제를 서버에 확정한다.
 *
 * GE 는 체크아웃을 띄우자마자 `GetCheckoutCartInfo` 로 가격을 읽고, 그 가격에 **카트 소유자의 발급
 * 쿠폰**이 녹는다(`libs/global-e/cart-coupons.ts`). 그래서 이 시점에 두 가지가 서버에 있어야 한다:
 *   1. **claim**(ownerUid) — `/international-checkout` 마운트 때도 부르지만 fire-and-forget 이라 GE
 *      iframe 의 첫 cart-info 보다 늦을 수 있다. 늦으면 그 가격엔 쿠폰이 없다. 그래서 여기서 **기다린다**.
 *      (멱등 — 같은 소유자의 재claim 은 아무것도 바꾸지 않는다.)
 *   2. **쿠폰 선택**(자동 적용 / 사용 안 함) — mgmt `couponOptOut`. 소유자 본인만 쓸 수 있다.
 *
 * 게스트(로그인 토큰 없음)·cart token 없음은 할 일이 없어 `true`. 선택 기록이 실패하면 `false` —
 * 호출부가 "사용 안 함" 이었다면 이동을 막아야 한다(안 막으면 원치 않는 쿠폰이 소진된다).
 */
export async function prepareGeCheckoutCoupons(
  optOut: boolean,
): Promise<boolean> {
  const cartToken = CartToken.get();
  if (!cartToken) return true;
  const token = await getToken();
  if (!token) return true;
  await claimGuestCartOwner();
  try {
    const res = await fetch(
      "/admin-api/api/v1/cart/guest-token/coupon-selection/",
      {
        method: "PUT",
        headers: {
          "content-type": "application/json",
          authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ cartToken, optOut }),
      },
    );
    return res.ok;
  } catch {
    return false;
  }
}

/**
 * 로그인 직후, 로그아웃 상태로 담아둔 게스트 카트를 현재 uid 카트로 채택하고
 * 게스트 도큐먼트를 삭제하도록 서버에 요청한다(전이 처리, §4.5).
 * 게스트 cartToken 이 없거나 로그인 토큰을 못 받으면 no-op.
 * ⚠️ GE 국제 체크아웃 흐름에선 호출 금지 — 게스트 문서 삭제로 GE 가 카트를 못 읽는다(§2.5).
 * 그 경로는 `claimGuestCartOwner` 사용. (호출 게이트: `CartLoginSync`)
 */
export async function mergeGuestCart(): Promise<void> {
  const cartToken = CartToken.get();
  if (!cartToken) return;
  const token = await getToken();
  if (!token) return;
  try {
    await fetch("/admin-api/api/v1/cart/merge/", {
      method: "POST",
      headers: {
        "content-type": "application/json",
        authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ cartToken }),
    });
  } catch {
    /* fire-and-forget */
  }
}

/**
 * 서버에 저장된 현재 카트의 items(sku/quantity)를 읽어온다.
 * - 로그인 유저: Bearer 로 `/api/v1/cart`.
 * - 게스트: `bh_cart_token` 으로 `/api/v1/guest-cart`.
 * 서버는 sku/quantity 만 보관하므로, 로컬 카트로 복원하려면 호출 측에서
 * 카탈로그(sku→product)로 slug/options 를 해석해야 한다.
 */
export async function getCartInfo(): Promise<ServerCartItem[]> {
  const token = await getToken();
  try {
    if (token) {
      const res = await fetch("/admin-api/api/v1/cart/", {
        headers: { authorization: `Bearer ${token}` },
      });
      return parseItems(res);
    }
    const cartToken = CartToken.get();
    if (!cartToken) return [];
    const res = await fetch(
      `/admin-api/api/v1/guest-cart/?cartToken=${encodeURIComponent(cartToken)}`,
    );
    return parseItems(res);
  } catch {
    return [];
  }
}

/**
 * 이 cart token 이 **Global-E 주문으로 소비됐는지** 서버에 묻는다(mgmt `GET /api/v1/guest-cart/consumed`
 * — 그 token 으로 만든 `GlobalE` draft 가 있는가). 결제가 안 끝난 카트는 `false` 다.
 *
 * 판정 불가(네트워크 오류·타임아웃·mgmt 미배포 404·응답 형식 이상)는 **`null`** — 호출부는 비우지 않는다.
 * 모르면 종전 동작(카트 유지)으로 떨어지는 쪽이 안전하다: 잘못 비우면 결제 안 한 카트가 사라진다.
 * 소비 흐름은 `libs/global-e/consumed-cart.ts`, 근거는 docs/global-e-storefront.md §4.7.3.
 */
export async function getGuestCartConsumed(
  cartToken: string,
  timeoutMs: number,
): Promise<boolean | null> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  // 판정 불가는 **사유를 남긴다** — `null` 은 호출부에서 `false`(아직 결제 전)와 같은 동작이 되므로, 조용히 두면
  // "mgmt 미배포·배선 오류" 와 "정상" 이 밖에서 구별되지 않는다(docs/pitfalls.md 「fail-closed 경로는 조용히 반환하지 말 것」).
  // token 은 찍지 않는다.
  try {
    const res = await fetch(
      `/admin-api/api/v1/guest-cart/consumed/?cartToken=${encodeURIComponent(cartToken)}`,
      { signal: ctrl.signal },
    );
    if (!res.ok) {
      console.warn(
        `[GlobalE] consumed lookup unavailable (HTTP ${res.status})`,
      );
      return null;
    }
    const result = await res.json();
    const consumed = result?.message?.consumed;
    if (typeof consumed !== "boolean") {
      console.warn("[GlobalE] consumed lookup: unexpected response shape");
      return null;
    }
    return consumed;
  } catch (e) {
    console.warn(
      `[GlobalE] consumed lookup failed (${ctrl.signal.aborted ? "timeout" : "network"})`,
      e,
    );
    return null;
  } finally {
    clearTimeout(timer);
  }
}

async function parseItems(res: Response): Promise<ServerCartItem[]> {
  if (!res.ok) return [];
  const result = await res.json();
  const items = result?.message?.items;
  if (!Array.isArray(items)) return [];
  return items.map((i: { sku: string; quantity: number }) => ({
    sku: i.sku,
    quantity: i.quantity,
  }));
}
